import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import { MultiSectionDisplay } from "@/components/MultiSectionDisplay";
import { RepurposeModal } from "@/components/RepurposeModal";
import { GenerationErrorBlock } from "@/components/GenerationErrorBlock";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AnimatePresence, motion } from "framer-motion";
import { FileUploadZone } from "@/components/FileUploadZone";
import { toast } from "sonner";
import { Newspaper, Lightning, ArrowsClockwise } from "@phosphor-icons/react";
import { TemplateManager } from "@/components/TemplateManager";
import { UsageWarningBanner } from "@/components/UsageWarningBanner";
import { UpgradeModal } from "@/components/UpgradeModal";
import { AlwaysIncludeIndicator } from "@/components/AlwaysIncludeIndicator";
import { useLocalDraft } from "@/hooks/useLocalDraft";
import { NEWSLETTER_TONES, NEWSLETTER_GOALS, NEWSLETTER_LOADING_MESSAGES } from "@/components/newsletter/newsletterConstants";

export default function NewsletterBuilderPage() {
  const { api, refreshUser } = useAuth();
  const location = useLocation();
  const { restore, save: saveDraft, clear: clearDraft } = useLocalDraft("nib_draft_newsletter");

  const [issueTopic, setIssueTopic] = useState("");
  const [coreInsight, setCoreInsight] = useState("");
  const [researchText, setResearchText] = useState("");
  const [newsletterName, setNewsletterName] = useState("My Newsletter");
  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [cta, setCta] = useState("");
  const [toneVariations, setToneVariations] = useState(["Direct", "Insightful"]);
  const [includeStory, setIncludeStory] = useState(true);
  const [includeTactical, setIncludeTactical] = useState(true);
  const [includeResource, setIncludeResource] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadedFileIds, setUploadedFileIds] = useState([]);
  const [aiModel, setAiModel] = useState("");
  const [availableModels, setAvailableModels] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [generatedNewsletter, setGeneratedNewsletter] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [repurposeOpen, setRepurposeOpen] = useState(false);
  const [alwaysIncludeFiles, setAlwaysIncludeFiles] = useState([]);
  const [generationError, setGenerationError] = useState(null);
  const [autoRetryCount, setAutoRetryCount] = useState(0);

  useEffect(() => {
    if (location.state?.prefill) return;
    const resumeDraft = location.state?.resumeDraft;
    if (resumeDraft) {
      const ctx = resumeDraft.generation_context || {};
      if (ctx.issue_topic || ctx.topic) setIssueTopic(ctx.issue_topic || ctx.topic);
      if (ctx.core_insight) setCoreInsight(ctx.core_insight);
      if (ctx.research_text) setResearchText(ctx.research_text);
      if (ctx.newsletter_name) setNewsletterName(ctx.newsletter_name);
      if (ctx.audience) setAudience(ctx.audience);
      if (ctx.goal) setGoal(ctx.goal);
      if (ctx.cta) setCta(ctx.cta);
      if (ctx.tone_variations?.length) setToneVariations(ctx.tone_variations);
      if (ctx.include_story !== undefined) setIncludeStory(ctx.include_story);
      if (ctx.include_tactical !== undefined) setIncludeTactical(ctx.include_tactical);
      if (ctx.include_resource !== undefined) setIncludeResource(ctx.include_resource);
      if (ctx.ai_model) setAiModel(ctx.ai_model);
      if (resumeDraft.sections && Object.keys(resumeDraft.sections).length > 0) {
        setGeneratedNewsletter({ sections: resumeDraft.sections, section_names: Object.keys(resumeDraft.sections) });
      }
      return;
    }
    const draft = restore();
    if (draft) {
      if (draft.issueTopic) setIssueTopic(draft.issueTopic);
      if (draft.coreInsight) setCoreInsight(draft.coreInsight);
      if (draft.researchText) setResearchText(draft.researchText);
      if (draft.newsletterName) setNewsletterName(draft.newsletterName);
      if (draft.audience) setAudience(draft.audience);
      if (draft.goal) setGoal(draft.goal);
      if (draft.cta) setCta(draft.cta);
      if (draft.toneVariations?.length) setToneVariations(draft.toneVariations);
      if (draft.includeStory !== undefined) setIncludeStory(draft.includeStory);
      if (draft.includeTactical !== undefined) setIncludeTactical(draft.includeTactical);
      if (draft.includeResource !== undefined) setIncludeResource(draft.includeResource);
      if (draft.aiModel) setAiModel(draft.aiModel);
    }
  }, [restore]);

  useEffect(() => {
    const timer = setTimeout(() => {
      saveDraft({ issueTopic, coreInsight, researchText, newsletterName, audience, goal, cta, toneVariations, includeStory, includeTactical, includeResource, aiModel });
    }, 500);
    return () => clearTimeout(timer);
  }, [issueTopic, coreInsight, researchText, newsletterName, audience, goal, cta, toneVariations, includeStory, includeTactical, includeResource, aiModel, saveDraft]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const resp = await api.get("/models/available");
        setAvailableModels(resp.data.models || []);
        if (resp.data.models?.length > 0) setAiModel(resp.data.models[0]);
      } catch (e) { console.error("Failed to fetch models:", e); }
    };
    fetchModels();
  }, [api]);

  useEffect(() => {
    const loadDefaults = async () => {
      try {
        const resp = await api.get("/profile");
        const p = resp.data;
        setAudience(prev => prev || p.default_audience || "");
        setToneVariations(prev => prev.length > 0 ? prev : (p.default_tone_variations || []));
        setAiModel(prev => prev || p.default_ai_model || "");
        setNewsletterName(prev => prev === "My Newsletter" ? (p.default_newsletter_name || prev) : prev);
        setCta(prev => prev || p.default_cta || "");
      } catch (e) { console.error("Failed to load defaults:", e); }
    };
    loadDefaults();
  }, [api]);

  useEffect(() => {
    const prefill = location.state?.prefill;
    if (prefill) {
      setIssueTopic(prefill.topic || "");
      setResearchText(prefill.research_material || prefill.research_text || "");
      setAudience(prefill.audience || "");
      setGoal(prefill.goal || "");
      setCta(prefill.cta === "NONE" ? "" : prefill.cta || "");
      setToneVariations(prefill.tone_variations || ["Direct", "Insightful"]);
      if (prefill.ai_model_used) setAiModel(prefill.ai_model_used);
      if (prefill.duplicate) toast.success("Configuration duplicated — ready to regenerate");
    }
  }, [location.state]);

  useEffect(() => {
    if (!generating) return;
    let idx = 0;
    setLoadingMsg(NEWSLETTER_LOADING_MESSAGES[0]);
    const interval = setInterval(() => {
      idx = (idx + 1) % NEWSLETTER_LOADING_MESSAGES.length;
      setLoadingMsg(NEWSLETTER_LOADING_MESSAGES[idx]);
    }, 3500);
    return () => clearInterval(interval);
  }, [generating]);

  const toggleTone = (tone) => {
    setToneVariations((prev) => prev.includes(tone) ? prev.filter((t) => t !== tone) : [...prev, tone]);
  };

  const sectionToggles = { includeStory, includeTactical, includeResource };
  const sectionSetters = { includeStory: setIncludeStory, includeTactical: setIncludeTactical, includeResource: setIncludeResource };

  const handleGenerate = async (e) => {
    if (e) e.preventDefault();
    if (!issueTopic || !coreInsight || !audience || !goal || !aiModel) {
      toast.error("Please fill in all required fields");
      return;
    }
    setGenerating(true);
    setGeneratedNewsletter(null);
    setIsSaved(false);
    setGenerationError(null);
    setAutoRetryCount(0);

    const fileIds = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const resp = await api.post("/upload", formData);
        fileIds.push(resp.data.file_id);
      } catch {
        toast.error(`Could not read ${file.name}. Please try a different format.`);
        fileIds.push(null);
      }
    }
    setUploadedFileIds(fileIds);

    try {
      const resp = await api.post("/newsletter/build", {
        issue_topic: issueTopic, core_insight: coreInsight, research_text: researchText,
        newsletter_name: newsletterName, audience, goal, cta: cta || "NONE",
        tone_variations: toneVariations, include_story: includeStory,
        include_tactical: includeTactical, include_resource: includeResource,
        ai_model: aiModel, file_ids: fileIds,
      });
      setGeneratedNewsletter(resp.data);
      clearDraft();
      toast.success("Newsletter built! +25 XP");
      refreshUser();
    } catch (err) {
      const data = err.response?.data;
      if (data?.error && data?.error_type) {
        setGenerationError(data);
        if (data.error_type === "rate_limit") setShowUpgrade(true);
      } else {
        if (err.response?.status === 429) setShowUpgrade(true);
        toast.error(data?.detail || "Newsletter generation failed.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (mergedSections) => {
    if (!generatedNewsletter) return;
    const sectionsToSave = mergedSections || generatedNewsletter.sections;
    const fullDraft = generatedNewsletter.section_names
      .map((name) => `### ${name}\n\n${sectionsToSave[name] || ""}`)
      .join("\n\n---\n\n");
    await api.post("/history", {
      title: `Newsletter: ${issueTopic.substring(0, 60)}`, topic: issueTopic,
      research_material: researchText, content_type: "Newsletter Issue", platform: "Substack",
      audience, goal, cta: cta || "NONE", offer_asset: "NONE", tone_variations: toneVariations,
      length_constraint: "FULL NEWSLETTER", ai_model_used: aiModel, generated_core_angle: coreInsight,
      generated_draft: fullDraft, generated_why_it_works: `Complete ${generatedNewsletter.section_names.length}-section newsletter`,
      generated_alternate_hooks: [], generated_cta_variations: null,
    });
    setIsSaved(true);
    refreshUser();
  };

  return (
    <NoBusinessGuard>
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div className="flex items-center gap-3">
          <Newspaper size={28} className="text-[#FF4400]" />
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight" data-testid="newsletter-page-title">Newsletter Builder</h1>
            <p className="text-sm text-[var(--text-muted)] mt-0.5">Build a complete, publication-ready newsletter issue</p>
          </div>
        </div>

        <UsageWarningBanner />

        <form onSubmit={handleGenerate} className="space-y-6" data-testid="newsletter-form">
          <FormField label="Newsletter Name">
            <Input value={newsletterName} onChange={(e) => setNewsletterName(e.target.value)} placeholder="e.g., The Blueprinted Brief" className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="newsletter-name-input" />
          </FormField>
          <FormField label="Issue Topic" required>
            <Input value={issueTopic} onChange={(e) => setIssueTopic(e.target.value)} placeholder="What is this issue about?" maxLength={255} required className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="newsletter-topic-input" />
          </FormField>
          <FormField label="Core Insight to Deliver" required>
            <Textarea value={coreInsight} onChange={(e) => setCoreInsight(e.target.value)} placeholder="The one thing the reader should understand differently..." maxLength={1000} required className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] min-h-[100px] resize-y" data-testid="newsletter-insight-input" />
          </FormField>
          <FormField label="Research / Source Material">
            <Textarea value={researchText} onChange={(e) => setResearchText(e.target.value)} placeholder="Paste notes, data, client stories, stats..." maxLength={50000} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] min-h-[120px] resize-y" data-testid="newsletter-research-input" />
            <div className="mt-3"><FileUploadZone files={files} onFilesChange={setFiles} uploadedFileIds={uploadedFileIds} /></div>
          </FormField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Audience" required>
              <Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="e.g., B2B SaaS founders, agency owners, solo consultants" required className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="newsletter-audience-input" />
            </FormField>
            <FormField label="Goal" required>
              <Select value={goal} onValueChange={setGoal}>
                <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="newsletter-goal-select"><SelectValue placeholder="Select goal" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
                  {NEWSLETTER_GOALS.map((g) => (<SelectItem key={g} value={g} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{g}</SelectItem>))}
                </SelectContent>
              </Select>
            </FormField>
            <div className="sm:col-span-2">
              <FormField label="CTA">
                <Input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="e.g., Book a strategy call, Download the guide" className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="newsletter-cta-input" />
              </FormField>
            </div>
          </div>

          <div>
            <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-3">Include Sections</label>
            <div className="flex flex-wrap gap-2" data-testid="newsletter-section-toggles">
              {[
                { label: "Story / Example", state: includeStory, setter: setIncludeStory, id: "story" },
                { label: "Tactical Breakdown", state: includeTactical, setter: setIncludeTactical, id: "tactical" },
                { label: "Resource of the Week", state: includeResource, setter: setIncludeResource, id: "resource" },
              ].map(({ label, state, setter, id }) => (
                <button key={label} type="button" onClick={() => setter(!state)} data-testid={`newsletter-toggle-${id}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${state ? "bg-[var(--accent-subtle)] border-[#FF4400] text-[#FF4400]" : "bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]"}`}
                >{state ? "\u2713 " : ""}{label}</button>
              ))}
            </div>
          </div>

          <ToneSelector tones={NEWSLETTER_TONES} selected={toneVariations} onToggle={toggleTone} prefix="newsletter" required />

          <ModelSelector models={availableModels} value={aiModel} onChange={setAiModel} prefix="newsletter" />

          <TemplateManager templateType="newsletter" currentValues={{ content_type: "Newsletter Issue", platform: "Substack", audience, goal, cta: cta || "NONE", tone_variations: toneVariations, ai_model: aiModel, newsletter_name: newsletterName, include_story: includeStory, include_tactical: includeTactical, include_resource: includeResource }}
            onLoad={(t) => { if (t.audience) setAudience(t.audience); if (t.goal) setGoal(t.goal); if (t.newsletter_name) setNewsletterName(t.newsletter_name); setCta(t.cta === "NONE" ? "" : t.cta || ""); if (t.tone_variations?.length) setToneVariations(t.tone_variations); if (t.include_story !== undefined) setIncludeStory(t.include_story); if (t.include_tactical !== undefined) setIncludeTactical(t.include_tactical); if (t.include_resource !== undefined) setIncludeResource(t.include_resource); if (t.ai_model && availableModels.includes(t.ai_model)) setAiModel(t.ai_model); }} />

          <AlwaysIncludeIndicator onFilesLoaded={setAlwaysIncludeFiles} />

          <AnimatePresence>
            {generationError && (<GenerationErrorBlock error={generationError} onRetry={() => handleGenerate()} onDismiss={() => { setGenerationError(null); setAutoRetryCount(0); }} autoRetryCount={autoRetryCount} onAutoRetryStart={() => setAutoRetryCount((c) => c + 1)} />)}
          </AnimatePresence>

          <button type="submit" disabled={generating || availableModels.length === 0} className="w-full bg-[#FF4400] hover:bg-[#FF6633] text-white font-semibold h-14 rounded-md text-base transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_0_20px_rgba(255,68,0,0.4)] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="newsletter-submit-button">
            {generating ? (<><Newspaper size={20} className="animate-pulse" />{loadingMsg}</>) : (<><Lightning size={20} weight="fill" />Build Newsletter Issue</>)}
          </button>
        </form>

        <AnimatePresence>
          {generatedNewsletter && (
            <>
              <MultiSectionDisplay data={generatedNewsletter} onSave={handleSave} isSaved={isSaved} exportMeta={{ title: issueTopic, content_type: "Newsletter Issue", platform: "Newsletter", audience, ai_model: aiModel }} resourcesUsed={alwaysIncludeFiles.map(f => f.display_name)} generationContext={{ content_type: "Newsletter Issue", topic: issueTopic, platform: "Newsletter", tone: toneVariations.join(", ") }} aiModel={aiModel} />
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-center">
                <button onClick={() => setRepurposeOpen(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-[var(--bg-raised)] border border-[var(--border-strong)] text-[var(--text-secondary)] text-sm font-medium hover:border-[#FF4400]/30 hover:text-[#FF4400] transition-all" data-testid="newsletter-repurpose-button">
                  <ArrowsClockwise size={16} />Repurpose This Newsletter
                </button>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        <RepurposeModal isOpen={repurposeOpen} onClose={() => setRepurposeOpen(false)} originalContent={generatedNewsletter ? generatedNewsletter.section_names.map((n) => `### ${n}\n\n${generatedNewsletter.sections[n] || ""}`).join("\n\n---\n\n") : ""} originalType="Newsletter Issue" originalPlatform="Substack" audience={audience} availableModels={availableModels} defaultTones={toneVariations} defaultModel={aiModel} />
      </main>
    </div>
    <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} trigger="build newsletter" />
    </NoBusinessGuard>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
        {label} {required && <span className="text-[#FF4400]">*</span>}
      </label>
      {children}
    </div>
  );
}

function ToneSelector({ tones, selected, onToggle, prefix, required }) {
  return (
    <div>
      <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-3">
        Tone {required && <span className="text-[#FF4400]">*</span>}
      </label>
      <div className="flex flex-wrap gap-2">
        {tones.map((tone) => (
          <button key={tone} type="button" onClick={() => onToggle(tone)} data-testid={`${prefix}-tone-${tone.toLowerCase()}`}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${selected.includes(tone) ? "bg-[var(--accent-subtle)] border-[#FF4400] text-[#FF4400]" : "bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-[#FF4400]/30 hover:text-[var(--text-primary)]"}`}
          >{tone}</button>
        ))}
      </div>
    </div>
  );
}

function ModelSelector({ models, value, onChange, prefix }) {
  return (
    <div>
      <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">AI Model <span className="text-[#FF4400]">*</span></label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid={`${prefix}-model-select`}><SelectValue placeholder="Select model" /></SelectTrigger>
        <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
          {models.map((m) => (<SelectItem key={m} value={m} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{m}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
  );
}
